import axios from "axios";
import { config } from "../../config";
import { logger } from "../../lib/logger";
import { ErrorCodes } from "../../lib/errorCodes";
import { fetchRawSnapshot } from "../../mini_server/fetchSnapshot";
import { LightUserUtils } from "../../types/leanTypes/lightProfile";
import { THelperSnapshot, TUserHelpersData } from "../../types/newTypes/helpersTypes";
import { EMAIL_HELPER_ADDRESS } from "../../utils/constants";
import { extractProfileFromRawSnapshot } from "../../utils/snapshotUtils";
import { TTrustor } from "../../types/newTypes/trustorsTypes";
import { TDbAccount } from "../../types/dbAccount";

type TUserSafe = {
  address: string;
  name: string;
  groups: string[];
};

export const fetchLightUserSnapshot = async ({ address }: { address: string }) => {
  try {
    const response = await fetchRawSnapshot({ address });
    if (!response) {
      throw new Error("Something went wrong while trying to fetch light user snapshot");
    }

    const lightUser = LightUserUtils.fromMap(response);

    return lightUser;
  } catch (error) {
    throw new Error(error);
  }
};

export const fetchSafesByUserAddress = async ({ address }: { address: string }) => {
  try {
    const response = await axios.get(
      `${config.nodeUrl}/snapshot/client?publickey=${address}&env=${config.nodeLab === "9093" ? "main" : "test"}`
    );
    if (response.status !== 200) {
      throw new Error("Something went wrong while trying to fetch snapshots");
    }
    const userSafes: TUserSafe[] =
      response.data.length === 0
        ? []
        : response.data.map((snapshot) => {
            const groups: string[] = snapshot.groups.map((group) => group.name);
            const { address, name } = snapshot;
            return { address, name, groups };
          });
    return userSafes;
  } catch (error) {
    logger.error("api", ErrorCodes.API_001, "fetchSafesByUserAddress error", error as Error);
    return [];
  }
};

export const fetchAccountBalances = async ({ address }: { address: string }) => {
  try {
    const response = await axios.get(
      `${config.fullNodeUrl}/cosmos/bank/v1beta1/balances/${address}`
    );
    if (response.status !== 200) {
      throw new Error("Somthing went wrong while trying to fetch snapshhot");
    }
    return response.data;
  } catch (error) {
    throw new Error(error);
  }
};

export const fetchAccountAllowances = async ({ address }: { address: string }) => {
  try {
    const response = await axios.get(
      `${config.fullNodeUrl}/cosmos/feegrant/v1beta1/allowances/${address}`
    );
    if (response.status !== 200) {
      throw new Error("Somthing went wrong while trying to fetch snapshhot");
    }
    return response.data;
  } catch (error) {
    throw new Error(error);
  }
};

export const fetchAccountByName = async ({
  name,
}: {
  name: string;
}): Promise<TDbAccount | null> => {
  try {
    const headers = {
      "Content-Type": "application/json",
      "api-key": import.meta.env.VITE_PROXY_API_KEY,
      env: "main",
    };
    const response = await axios.get(
      `${config.proxyUrl}/users/accounts/getAccountByName/?accountName=${name}`,
      { headers }
    );
    if (response.status !== 200) {
      throw new Error(`Somthing went wrong while trying to fetch ${name}`);
    }

    if (response.data && (!response.data?.public_key || !response.data?.name)) {
      return null;
    }

    if (!response.data || !response.data?.public_key || !response.data?.name) {
      // if the account is not found, the response will be an empty object
      throw new Error(`No account found for ${name}`);
    }
    return response.data as TDbAccount;
  } catch (error) {
    throw new Error(error);
  }
};

export const newFetcNParsehHelpersSnapshots = async (addresses: string[]) => {
  const parsedHelpers: THelperSnapshot[] = [];

  for (let i = 0; i < addresses.length; i++) {
    const helperLightSnapshot = await fetchLightUserSnapshot({ address: addresses[i] });
    if (helperLightSnapshot) {
      parsedHelpers.push({
        address: addresses[i],
        public_key: helperLightSnapshot.public_key,
        name: helperLightSnapshot.name,
        helperAddresses: helperLightSnapshot.helperAddresses,
      });
    }
  }
  return parsedHelpers;
};

export const fetchUserHelpersDataByUserAddress = async ({
  address,
  ignoreEmailHelper = true,
}: {
  address: string;
  ignoreEmailHelper: boolean | undefined;
}) => {
  const helpersData: TUserHelpersData = {
    helpers: [],
    threshold: "",
    policySignature: "",
  };
  try {
    const snapshot = await fetchRawSnapshot({ address });
    if (!snapshot) {
      throw new Error("Something went wrong while trying to fetch user helpers");
    }
    const primaryGroup = snapshot.groups.find((group) => group.name === "Primary");
    if (!primaryGroup) {
      throw new Error("Primary group not found in snapshot");
    }
    const updateUserAddressPolicy = primaryGroup.nestedPolicies.find(
      (policy) => policy.id === "policy-allow-updateUserAddress"
    );
    if (!updateUserAddressPolicy) {
      throw new Error("Policy 'policy-allow-updateUserAddress' not found in snapshot");
    }
    const updateUserAddressCondition = updateUserAddressPolicy.object.conditions.find(
      (condition) => condition["class"] === "condition.allow_updateUserAddress"
    );
    if (!updateUserAddressCondition) {
      throw new Error("Condition 'condition.allow_updateUserAddress' not found in snapshot");
    }
    const allowedSourceParam = updateUserAddressCondition.params.find(
      (param) => param.key === "allowed_source"
    );
    const thresholdParam = updateUserAddressCondition.params.find(
      (param) => param.key === "threshold"
    );
    if (!allowedSourceParam || !thresholdParam) {
      throw new Error("Parameters 'allowed_source' or 'threshold' not found in condition");
    }

    let helpers: THelperSnapshot[] = [];

    const helperAddresses = allowedSourceParam.value;

    if (helperAddresses || !Array.isArray(helperAddresses)) {
      const parsedHelpers = await newFetcNParsehHelpersSnapshots(
        helperAddresses.filter((ha) => ha !== EMAIL_HELPER_ADDRESS)
      );
      helpers = [...parsedHelpers];
    }

    const emailHelperIndex = helperAddresses.findIndex((h) => h === EMAIL_HELPER_ADDRESS);

    if (!ignoreEmailHelper && emailHelperIndex >= 0) {
      helpers.push({
        address: EMAIL_HELPER_ADDRESS,
        public_key: "",
        name: "Email Helper",
        helperAddresses: [],
      });
    }

    helpersData.helpers = [...helpers];
    helpersData.threshold = thresholdParam.value;
    helpersData.policySignature = updateUserAddressPolicy.object.SIGNATURE || "";
  } catch (error) {
    logger.error(
      "api",
      ErrorCodes.API_001,
      "fetchUserHelpersDataByUserAddress error",
      error as Error
    );
  }
  return helpersData;
};

export const fetchUserTrustors = async ({ address }: { address: string }): Promise<TTrustor[]> => {
  try {
    const snapshot = await fetchRawSnapshot({ address });
    // if (!map) {
    //     console.error("Invalid map provided to LightUserUtils.fromMap");
    //     return LightUserUtils.empty();
    // }
    // if (!map["address"]) {
    //     console.error("Map does not contain 'address' field:", map);
    //     return LightUserUtils.empty();
    // }
    // if (!map["name"]) {
    //     console.error("Map does not contain 'name' field:", map);
    //     return LightUserUtils.empty();
    // }
    // if (!Array.isArray(map["groups"]) ||  map["groups"].length === 0) {
    //     console.error("Map 'groups' field is not an array:", map);
    //     return LightUserUtils.empty();
    // }
    if (!snapshot) {
      throw new Error("Something went wrong while trying to fetch user trustors");
    }

    const profileObject = extractProfileFromRawSnapshot(snapshot)["object"];
    if (!profileObject) {
      throw new Error("Profile object not found in snapshot");
    }
    const allTrustors = profileObject.trustors;
    if (!allTrustors) {
      throw new Error("Trustors not found in profile object");
    }

    const trustors = allTrustors.map((trustor) => {
      return {
        address: trustor.address,
        name: trustor.name,
        message: trustor.message,
      } as TTrustor;
    });
    return trustors;
    // const trustors = primaryGroup.nestedPolicies.find(
  } catch (error) {
    throw new Error(error);
  }
};
